import { dijkstra } from "utils/math/dijkstra";
import { reconstructPath } from "utils/math/geometry/reconstructPath";
import { countEdgeUsage } from "utils/math/geometry/countEdgeUsage";
import { calculatePathUtilization } from "utils/math/geometry/calculatePathUtilization";
import { buildAugmentedGraph } from "utils/math/geometry/buildAugmentedGraph";
import { roundTo } from "./utils";
import {
  FindTheShortestCablePathArgs,
  FindTheShortestCablePathResult,
  PathCoord,
  DestinationResult,
} from "./types";

/**
 * Processes a single circuit: builds the graph, runs pathfinding, and calculates metrics.
 */
export const findTheShortestCablePath = ({
  circuitId,
  circuitData,
  source,
  topology,
  sourceId,
}: FindTheShortestCablePathArgs): FindTheShortestCablePathResult => {
  const destinations = circuitData.destinations;

  if (!destinations || destinations.length === 0) {
    return {
      circuit_id: circuitId,
      source_id: sourceId,
      error: "No destinations found",
      breaker: "",
      cable_spec: "",
      destination_description: "",
      total_cable_length: 0,
      shared_trunk_length: 0,
      shared_percentage: 0,
      bridges_used: 0,
      destinations_count: 0,
      destinations: [],
    };
  }

  const {
    graph,
    nodes,
    sourceId: sourceNodeId,
    destinationIds,
  } = buildAugmentedGraph({
    topology,
    source,
    destinations,
  });

  const { distances, previous } = dijkstra({
    graph,
    startNodeId: sourceNodeId,
  });

  // Reconstruct Paths
  const paths: string[][] = [];
  const destResults: DestinationResult[] = [];

  for (let idx = 0; idx < destinationIds.length; idx++) {
    const destId = destinationIds[idx];
    const path = reconstructPath({
      previous,
      startNodeId: sourceNodeId,
      endNodeId: destId,
    });

    const dist = distances[destId] ?? Infinity;
    const reachable = path.length > 0 && dist !== Infinity;

    // Convert NodeMap to PathCoords
    const pathCoords: PathCoord[] = [];
    if (reachable) {
      for (const nodeId of path) {
        const coord = nodes[nodeId];
        if (!coord) continue;
        pathCoords.push({
          x: coord.x,
          y: coord.y,
          node_id: nodeId,
        });
      }
    }

    const dest = destinations[idx];

    destResults.push({
      element_id: dest.element_id,
      element_name: dest.element_name,
      coordinates: dest.coordinates,
      path_length: reachable ? dist : null,
      reachable,
      path_nodes: reachable ? path : [],
      path_coords: pathCoords,
    });

    if (reachable) paths.push(path);
  }

  // Calculate Metrics (Shared Cable)
  const edgeCounts = countEdgeUsage({ paths });
  const { totalLength, sharedLength } = calculatePathUtilization({
    edgeCounts,
    nodes,
  });

  const sharedPct = totalLength > 0 ? (sharedLength / totalLength) * 100 : 0;

  const sld = circuitData.sld_element ?? {};

  const result: FindTheShortestCablePathResult = {
    circuit_id: circuitId,
    source_id: sourceId,

    breaker: sld.breaker ?? "",
    cable_spec: sld.cable_spec ?? "",
    destination_description: sld.destination ?? "",

    total_cable_length: roundTo(totalLength, 2),
    shared_trunk_length: roundTo(sharedLength, 2),
    shared_percentage: roundTo(sharedPct, 1),

    bridges_used: 0, // TODO:
    destinations_count: destinations.length,

    destinations: destResults,
  };

  if (circuitData.lv_cable_match_id !== undefined) {
    result.lv_cable_match_id = circuitData.lv_cable_match_id;
  }
  if (circuitData.lv_cable_match_name !== undefined) {
    result.lv_cable_match_name = circuitData.lv_cable_match_name;
  }
  if (circuitData.cable_reasoning !== undefined) {
    result.cable_reasoning = circuitData.cable_reasoning;
  }

  return result;
};
